import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  User,
  Workout,
  Exercise,
  Routine
} from './workout-tracker.model';

@Injectable({ providedIn: 'root' })
export class WorkoutTrackerService {
  private injector = inject(EnvironmentInjector);

  constructor(private db: AngularFireDatabase) {}

  /**
   * AngularFire 20 compat (AngularFireDatabase.object/list) calls inject(NgZone)
   * internally, so it must run inside an injection context. Many of these methods
   * are invoked lazily from RxJS operators (e.g. switchMap), which are not injection
   * contexts — so wrap every db access in the service's captured injector.
   */
  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  // User CRUD
  getUser(userId: string): Observable<User | null> {
    return this.inCtx(() => this.db.object<User>(`users/${userId}`).valueChanges());
  }
  setUser(user: User) {
    return this.inCtx(() => this.db.object(`users/${user.id}`).set(user));
  }

  // Workout CRUD
  getWorkouts(userId: string): Observable<Workout[]> {
    return this.inCtx(() => this.db.list<Workout>(`workouts`, ref => ref.orderByChild('userId').equalTo(userId)).valueChanges());
  }
  getWorkout(workoutId: string): Observable<Workout | null> {
    return this.inCtx(() => this.db.object<Workout>(`workouts/${workoutId}`).valueChanges());
  }
  addWorkout(workout: Workout) {
    return this.inCtx(() => this.db.object(`workouts/${workout.id}`).set(workout));
  }
  updateWorkout(workout: Workout) {
    return this.inCtx(() => this.db.object(`workouts/${workout.id}`).update(workout));
  }
  deleteWorkout(workoutId: string) {
    return this.inCtx(() => this.db.object(`workouts/${workoutId}`).remove());
  }

  // Exercise CRUD
  getExercises(): Observable<Exercise[]> {
    return this.inCtx(() => this.db.list<Exercise>('exercises').snapshotChanges().pipe(
      map(actions =>
        actions.map(a => ({
          id: a.key,
          ...(a.payload.val() as Exercise)
        }))
      )
    ));
  }
  getExercise(exerciseId: string): Observable<Exercise | null> {
    return this.inCtx(() => this.db.object<Exercise>(`exercises/${exerciseId}`).valueChanges());
  }
  addExercise(exercise: Exercise) {
    return this.inCtx(() => this.db.object(`exercises/${exercise.id}`).set(exercise));
  }
  updateExercise(exercise: Exercise) {
    return this.inCtx(() => this.db.object(`exercises/${exercise.id}`).update(exercise));
  }
  deleteExercise(exerciseId: string) {
    return this.inCtx(() => this.db.object(`exercises/${exerciseId}`).remove());
  }

  // Routine CRUD
  getRoutines(userId: string): Observable<Routine[]> {
    return this.inCtx(() => this.db.list<Routine>(`routines`, ref => ref.orderByChild('userId').equalTo(userId)).valueChanges());
  }
  getRoutine(routineId: string): Observable<Routine | null> {
    return this.inCtx(() => this.db.object<Routine>(`routines/${routineId}`).valueChanges());
  }
  addRoutine(routine: Routine) {
    return this.inCtx(() => this.db.object(`routines/${routine.id}`).set(routine));
  }
  updateRoutine(routine: Routine) {
    return this.inCtx(() => this.db.object(`routines/${routine.id}`).update(routine));
  }
  deleteRoutine(routineId: string) {
    return this.inCtx(() => this.db.object(`routines/${routineId}`).remove());
  }
}
